import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchLinerTypes } from '../data-provider';
import type { LinerType, LinerTypeFilters, LinerTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal liner type lookup cache used to resolve liner type references
 * without repeatedly requesting the same liner type data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const linerTypesQueryOptions = (
  filters: Partial<LinerTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const linerTypes = await fetchLinerTypes(filters, { signal });
      put(linerTypes.list);
      return linerTypes;
    },
    queryKey: CACHE_KEYS.linerTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_LINER_TYPES_CACHE_KEY = btoa('internal-liner-types-cache-key');

const INTERNAL_QUERY_LINER_TYPES_KEY = linerTypesQueryOptions({ codeDesc: INTERNAL_LINER_TYPES_CACHE_KEY }).queryKey;

const getLinerTypeFallback = (linerTypeId: string): LinerType => {
  logger.warn(`Liner type "${linerTypeId}" was not found. Verify the data model.`);
  return {
    codeDesc: linerTypeId,
    codeId: linerTypeId,
    id: linerTypeId,
    label: linerTypeId,
    lookupCodeType: 'LINERTYP',
  };
};

export const put = async (linerTypes: LinerType[]) => {
  // Sync with storage (currently React Query; may use IndexedDB in the future).
  const data = queryClient.getQueryData(INTERNAL_QUERY_LINER_TYPES_KEY);

  const newLinerTypes = linerTypes.reduce(
    (acc, linerType) => {
      acc[String(linerType.id)] = linerType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_LINER_TYPES_KEY, {
    list: Object.values(newLinerTypes),
    map: newLinerTypes,
  });
};

export const getAll = (): LinerTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_LINER_TYPES_KEY);
};

export const find = async (linerTypeId: string): Promise<LinerType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_LINER_TYPES_KEY);

  const linerType = data?.map[linerTypeId];
  if (linerType) {
    return linerType;
  }

  try {
    const newData = await queryClient.query(linerTypesQueryOptions({ search: { id: linerTypeId } }));
    if (newData.map[linerTypeId]) {
      void put(newData.list);
      return newData.map[linerTypeId];
    }
  } catch {
    return getLinerTypeFallback(linerTypeId);
  }

  return getLinerTypeFallback(linerTypeId);
};

export const findAll = async (linerTypeIds: string[]): Promise<LinerType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_LINER_TYPES_KEY);

  const missingIds: string[] = [];
  const linerTypes: LinerType[] = [];

  for (const linerTypeId of linerTypeIds) {
    const lt = data?.map[linerTypeId];
    if (lt) {
      linerTypes.push(lt);
    } else {
      missingIds.push(linerTypeId);
    }
  }

  if (!missingIds.length) {
    return linerTypes;
  }

  try {
    const newData = await queryClient.query(linerTypesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const lt = newData.map[missingId];
      if (lt) {
        linerTypes.push(lt);
      } else {
        linerTypes.push(getLinerTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      linerTypes.push(getLinerTypeFallback(missingId));
    }
  }

  return linerTypes;
};
